import {
  VUEX_ASSIGNMENT_ACTION_NAMES,
  VUEX_ASSIGNMENT_NAMESPACE,
  VUEX_CONTENT_ACTION_NAMES,
  VUEX_CONTENT_NAMESPACE,
  VUEX_EXERCISE_ACTION_NAMES,
  VUEX_EXERCISE_MUTATION_NAMES,
  VUEX_EXERCISE_GETTER_NAMES,
  VUEX_ROOT_GETTER_NAMES,
  ROUTES
} from "@tt-app/constants";
import { api } from "@tt-app/api";
import { getAuthInfo } from "@tt-app/utils";
import router from "@tt-app/router";
import {
  ExerciseActionContext,
  ExerciseNavigationAttemptPayload,
  ExerciseState,
  FetchExercisePayload
} from "@tt-app/types";
import {
  TerratalkLiveGetterNames,
  TerratalkLiveNamespace
} from "@tt-app/store/terratalkLive/types";

const { DETAILS_REQUEST } = VUEX_ASSIGNMENT_ACTION_NAMES;
const { COURSE_DETAILS_REQUEST } = VUEX_CONTENT_ACTION_NAMES;
const { FLAT_EXERCISE_LIST } = VUEX_ROOT_GETTER_NAMES;

const {
  ATTEMPT_EXERCISE_CLOSE,
  CLEAR_EXERCISE,
  EXERCISE_DETAILS_REQUEST,
  FETCH_EXERCISE,
  HINTS_DISPLAY_HIDE,
  HINTS_DISPLAY_SHOW,
  HINTS_DISPLAY_TOGGLE,
  INCREMENT_PAGE,
  LOG_ANSWER,
  LOG_END,
  LOG_EXERCISE_ANSWER,
  LOG_EXERCISE_END,
  LOG_EXERCISE_START,
  LOG_START,
  NAVIGATE_TO_NEXT,
  NAVIGATION_CANCELLATION,
  NAVIGATION_CONFIRMATION,
  NAVIGATION_MODAL_CLOSE,
  NAVIGATION_MODAL_OPEN,
  NAVIGATION_ATTEMPT,
  RESET_EXERCISE,
  RESTART_EXERCISE,
  SETTINGS_POPUP_CLOSE,
  SETTINGS_POPUP_OPEN,
  SETTINGS_POPUP_TOGGLE
} = VUEX_EXERCISE_ACTION_NAMES;

const {
  FINAL_QUESTION,
  LOG_ANSWER_PARAMS,
  LOG_END_PARAMS,
  LOG_START_PARAMS,
  REVIEW_PAGE
} = VUEX_EXERCISE_GETTER_NAMES;

const {
  ANSWER,
  EXERCISE_DETAILS,
  EXERCISE_LOADING,
  EXERCISE,
  EXERCISE_ACCURACY,
  EXERCISE_LOG_ID,
  EXERCISE_RESULTS,
  HINTS_SETTING,
  IN_PROGRESS,
  LIMITED_MODE_UPDATED,
  LOADING,
  NAVIGATION_MODAL_STATUS,
  NAVIGATION_NEXT_FUNCTION,
  NEXT_PAGE,
  PROCESSING,
  SETTINGS_POPUP_STATUS,
  TOTAL_STATE_RESET
} = VUEX_EXERCISE_MUTATION_NAMES;

const exerciseActions = {
  [ATTEMPT_EXERCISE_CLOSE]: async ({ getters, rootState, state }: ExerciseActionContext) => {
    const limitedModeDetails = (rootState as any).terratalkLive.limitedModeDetails;

    const route = router.app.$route;

    const assignmentId = route.params.assignmentId;
    const courseUuid = route.params.courseUuid;
    const isReview = getters[REVIEW_PAGE];
    const logId = state.logId;

    const feedbackRouteParams = {
      withFeedback: Boolean(!isReview).toString(),
      logId: logId?.toString()
    };

    let closeRoute: any;
    if (limitedModeDetails) {
      closeRoute = {
        name: ROUTES.TERRATALK_LIVE.LOBBY.NAME
      };
    } else if (route.params.assignmentId) {
      closeRoute = {
        name: ROUTES.ASSIGNMENT.DETAILS.NAME,
        params: {
          assignmentId
        }
      };
    } else {
      closeRoute = {
        name: ROUTES.COURSE.DETAILS.NAME,
        params: {
          courseUuid
        }
      };
    }

    closeRoute.params = {
      assignmentId,
      feedbackParams: feedbackRouteParams
    };

    await router.push(closeRoute);
  },
  [CLEAR_EXERCISE]: async ({ commit }: any) => {
    commit(EXERCISE, undefined);
  },
  [FETCH_EXERCISE]: async ({ commit, rootGetters }: any, payload: FetchExercisePayload) => {
    try {
      commit(LOADING, true);

      const authInfo = await getAuthInfo(rootGetters);

      const payloadWithAuthInfo = {
        ...authInfo,
        ...payload
      };

      const { data } = await api.exercise.fetchExercise(payloadWithAuthInfo);

      commit(EXERCISE, data);
    } catch (error) {
      console.log(`${FETCH_EXERCISE} failed`, error);
    } finally {
      commit(LOADING, false);
    }
  },
  [HINTS_DISPLAY_HIDE]({ commit }: any) {
    commit(HINTS_SETTING, false);
  },
  [HINTS_DISPLAY_SHOW]({ commit }: any) {
    commit(HINTS_SETTING, true);
  },
  [HINTS_DISPLAY_TOGGLE]({ state, dispatch }: any) {
    const hintsOn = state.settings.hints;

    if (hintsOn) {
      dispatch(HINTS_DISPLAY_HIDE);
    } else {
      dispatch(HINTS_DISPLAY_SHOW);
    }
  },
  [INCREMENT_PAGE]({ commit }: any) {
    commit(NEXT_PAGE);
  },
  [LOG_ANSWER]: async ({ commit, dispatch, getters, rootGetters }: any, payload: any) => {
    try {
      commit(PROCESSING, true);

      const authInfo = await getAuthInfo(rootGetters);

      const finalQuestion = getters[FINAL_QUESTION];
      const logAnswerParams = getters[LOG_ANSWER_PARAMS];
      const problemUuid = logAnswerParams.problemUuid;

      const payloadWithAuthInfo = {
        ...authInfo,
        ...logAnswerParams,
        ...payload
      };

      const { data } = await api.exercise.logExerciseAnswer(payloadWithAuthInfo);

      commit(EXERCISE_ACCURACY, {
        problemUuid,
        result: data
      });

      if (finalQuestion) {
        dispatch(LOG_END);
      }

      commit(ANSWER, { problemUuid, ...payload });
      dispatch(INCREMENT_PAGE);
    } catch (error) {
      console.log(`${LOG_ANSWER} failed`, error);
    } finally {
      commit(PROCESSING, false);
    }
  },
  [LOG_END]: async ({ commit, getters, rootGetters }: any) => {
    try {
      commit(PROCESSING, true);

      const authInfo = await getAuthInfo(rootGetters);

      const endParams = getters[LOG_END_PARAMS];

      const payloadWithAuthInfo = {
        ...authInfo,
        ...endParams
      };

      const { data } = await api.exercise.logExerciseEnd(payloadWithAuthInfo);

      commit(IN_PROGRESS, false);
      commit(EXERCISE_RESULTS, data);
    } catch (error) {
      console.log(`${LOG_EXERCISE_END} failed`, error);
    } finally {
      commit(PROCESSING, false);
    }
  },
  [LOG_EXERCISE_ANSWER]: async ({ commit, rootGetters }: any, payload: any) => {
    try {
      const authInfo = await getAuthInfo(rootGetters);

      const payloadWithAuthInfo = {
        ...authInfo,
        ...payload
      };

      const { data } = await api.exercise.logTranslationQuizAnswers(payloadWithAuthInfo);

      commit(EXERCISE_ACCURACY, {
        problemUuid: payload.problemUuid,
        result: data
      });
    } catch (error) {
      console.log(`${LOG_EXERCISE_ANSWER} failed`, error);
    }
  },
  [LOG_EXERCISE_END]: async ({ commit, rootGetters }: any, payload: any) => {
    try {
      const authInfo = await getAuthInfo(rootGetters);

      const payloadWithAuthInfo = {
        ...authInfo,
        ...payload
      };

      const { data } = await api.exercise.logExerciseEnd(payloadWithAuthInfo);

      commit(EXERCISE_RESULTS, data);
    } catch (error) {
      console.log(`${LOG_EXERCISE_END} failed`, error);
    }
  },
  [LOG_EXERCISE_START]: async ({ commit, rootGetters }: any, payload: any) => {
    try {
      const authInfo = await getAuthInfo(rootGetters);

      const payloadWithAuthInfo = {
        ...authInfo,
        ...payload
      };

      const { data } = await api.exercise.logExerciseStart(payloadWithAuthInfo);

      commit(EXERCISE_LOG_ID, data);
    } catch (error) {
      console.log(`${LOG_EXERCISE_START} failed`, error);
    }
  },
  [LOG_START]: async ({ commit, getters, rootGetters }: any, payload: Record<string, number>) => {
    try {
      commit(PROCESSING, true);

      const authInfo = await getAuthInfo(rootGetters);

      const logStartParams = getters[LOG_START_PARAMS];
      const activeSession =
        rootGetters[`${TerratalkLiveNamespace}/${TerratalkLiveGetterNames.ACTIVE_SESSION}`];

      const payloadWithAuthInfo = {
        assignmentId: payload.assignmentId,
        ...authInfo,
        ...logStartParams,
        limitedModeId: activeSession?.limitedMode?.id,
        sessionId: activeSession?.id
      };

      const { data } = await api.exercise.logExerciseStart(payloadWithAuthInfo);

      commit(IN_PROGRESS, true);
      commit(EXERCISE_LOG_ID, data);
    } catch (error) {
      console.log(`${LOG_START} failed`, error);
    } finally {
      commit(PROCESSING, false);
    }
  },
  [NAVIGATE_TO_NEXT]: async ({ state }: { getters: any; state: ExerciseState }) => {
    const nextFunction = state.modals.navigation.next;

    if (nextFunction) {
      nextFunction();
    }
  },
  [NAVIGATION_ATTEMPT]: async (
    { commit, dispatch, state }: { commit: any; dispatch: any; state: ExerciseState },
    payload: ExerciseNavigationAttemptPayload
  ) => {
    commit(NAVIGATION_NEXT_FUNCTION, payload);
    commit(LIMITED_MODE_UPDATED, payload);

    if (state.inProgress) {
      payload.next(false);
      dispatch(NAVIGATION_MODAL_OPEN);
    } else {
      dispatch(NAVIGATE_TO_NEXT);
    }
  },
  [NAVIGATION_CANCELLATION]: async ({ dispatch }: any) => {
    dispatch(NAVIGATION_MODAL_CLOSE);
  },
  [NAVIGATION_CONFIRMATION]: async ({ dispatch }: any) => {
    dispatch(NAVIGATE_TO_NEXT);
  },
  [NAVIGATION_MODAL_CLOSE]({ commit }: any) {
    commit(NAVIGATION_MODAL_STATUS, false);
  },
  [NAVIGATION_MODAL_OPEN]({ commit }: any) {
    commit(NAVIGATION_MODAL_STATUS, true);
  },
  [RESET_EXERCISE]: async ({ commit }: any) => {
    try {
      commit(TOTAL_STATE_RESET);
    } catch (error) {
      console.log(`${RESET_EXERCISE} failed`, error);
    }
  },
  [RESTART_EXERCISE]: async ({ commit }: any) => {
    try {
      commit(EXERCISE_ACCURACY, undefined);
      commit(EXERCISE_RESULTS, undefined);
    } catch (error) {
      console.log(`${RESTART_EXERCISE} failed`, error);
    }
  },
  [SETTINGS_POPUP_CLOSE]: async ({ commit }: any) => {
    commit(SETTINGS_POPUP_STATUS, false);
  },
  [SETTINGS_POPUP_OPEN]: async ({ commit }: any) => {
    commit(SETTINGS_POPUP_STATUS, true);
  },
  [SETTINGS_POPUP_TOGGLE]: async ({ state, dispatch }: any) => {
    const popupStatus = state.settings.modal.open;

    if (popupStatus) {
      dispatch(SETTINGS_POPUP_CLOSE);
    } else {
      dispatch(SETTINGS_POPUP_OPEN);
    }
  },
  [EXERCISE_DETAILS_REQUEST]: async ({ commit, dispatch, rootGetters }: any, payload: any) => {
    commit(EXERCISE_LOADING, true);
    commit(EXERCISE_DETAILS, undefined);

    if (payload.assignmentId) {
      await dispatch(
        `${VUEX_ASSIGNMENT_NAMESPACE}/${DETAILS_REQUEST}`,
        {
          assignmentId: payload.assignmentId
        },
        { root: true }
      );
    } else {
      await dispatch(
        `${VUEX_CONTENT_NAMESPACE}/${COURSE_DETAILS_REQUEST}`,
        {
          courseUuid: payload.courseUuid
        },
        { root: true }
      );
    }

    const exerciseList = rootGetters[FLAT_EXERCISE_LIST]({
      assignmentId: payload.assignmentId,
      courseUuid: payload.courseUuid
    });

    const exercise = exerciseList.filter((exercise: any) => {
      return exercise.uuid === payload.exerciseUuid;
    });

    const authInfo = await getAuthInfo(rootGetters);

    const exerciseType = exercise?.[0]?.type || exercise?.[0]?.exerciseType;
    const exerciseUuid = exercise?.[0]?.uuid;

    const exerciseParams = {
      ...authInfo,
      courseUuid: payload.courseUuid,
      exerciseType,
      exerciseUuid,
      assignmentId: payload.assignmentId
    };

    const response = await api.exercise.getExercise(exerciseParams);

    commit(EXERCISE_DETAILS, response);
    commit(EXERCISE_LOADING, false);
  }
};

export { exerciseActions };
